import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Space, Form, Input, Select, DatePicker, InputNumber, Row, Col, Typography, Divider, Popconfirm, App, Tag, Tabs, Timeline, Statistic, List, Avatar, Alert, Spin, Progress, Steps } from 'antd';
import { UserOutlined, ProjectOutlined, PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, ArrowLeftOutlined, TeamOutlined, BulbOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, CrownOutlined, ToolOutlined, MinusCircleOutlined, CloseOutlined, SaveOutlined, BarChartOutlined, FileTextOutlined, SearchOutlined, FilterOutlined, ClearOutlined } from '@ant-design/icons';
import { MILESTONE_OPTIONS, getMilestoneColor, getMilestoneLabel, getMilestoneDescription } from '../../constants/milestones';
import { useAdminStore } from '../../store/useAdminStore';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

const { TextArea } = Input;

const ProjectManagement = () => {
  const location = useLocation();
  const { projects, employees, loading, fetchProjects, fetchEmployees, createProject, updateProject, deleteProject } = useAdminStore();
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectActivities, setProjectActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [projectStats, setProjectStats] = useState({});
  const [editingMilestone, setEditingMilestone] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [currentView, setCurrentView] = useState('list');
  const [form] = Form.useForm();
  const [hoursBreakup, setHoursBreakup] = useState([]);
  const [activeTab, setActiveTab] = useState('project-info');
  const [editingMember, setEditingMember] = useState(null);
  const { message } = App.useApp();
  
  // Search and filter states
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  
  // Table state persistence
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [tableFilters, setTableFilters] = useState({});
  const [tableSorter, setTableSorter] = useState({});

  useEffect(() => {
    if (location.state && location.state.from === "dashboard") {
      handleAdd();
    }
  }, [location.state]);

  useEffect(() => {
    fetchProjects();
    fetchEmployees(); // Fetch employees for team assignment
  }, [fetchProjects, fetchEmployees]);

  // useEffect(() => {
  //   console.log('All employees:', employees);
  //   console.log('Project Managers:', employees.filter(emp => emp.role === 'project_manager' && emp.isActive));
  //   console.log('Employees for team:', employees.filter(emp => emp.role === 'employee' && emp.isActive));
  // }, [employees]);

  // Filtered and searched project data
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      // Search filter
      const searchLower = searchText.toLowerCase();
      const matchesSearch = !searchText || 
        project.name?.toLowerCase().includes(searchLower) ||
        project.client?.toLowerCase().includes(searchLower) ||
        project.description?.toLowerCase().includes(searchLower);
      
      // Status filter
      const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
      
      // Client filter
      const matchesClient = filterClient === 'all' || project.client === filterClient;
      
      // Active filter
      const matchesActive = filterActive === 'all' || 
        (filterActive === 'active' && project.isActive) ||
        (filterActive === 'inactive' && !project.isActive);
      
      return matchesSearch && matchesStatus && matchesClient && matchesActive;
    });
  }, [projects, searchText, filterStatus, filterClient, filterActive]);

  // Get unique clients from projects
  const clients = useMemo(() => {
    const clientList = [...new Set(projects.map(proj => proj.client).filter(Boolean))];
    return clientList.sort();
  }, [projects]);

  const handleClearFilters = () => {
    setSearchText('');
    setFilterStatus('all');
    setFilterClient('all');
    setFilterActive('all');
    setTableFilters({});
  };

  const handleAdd = () => {
    setSelectedProject(null);
    form.resetFields();
    setHoursBreakup([]);
    setActiveTab('project-info');
    setCurrentView('create');
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    const hoursBreakupData = project.hoursBreakup || [];
    setHoursBreakup(hoursBreakupData);
    
    form.setFieldsValue({
      name: project.name,
      description: project.description,
      client: project.client,
      status: project.status,
      startDate: project.startDate ? dayjs(project.startDate) : null,
      endDate: project.endDate ? dayjs(project.endDate) : null,
      projectManager: Array.isArray(project.projectManager)
        ? project.projectManager.map(pm => pm._id || pm)
        : [],
      techManager: Array.isArray(project.techManager)
        ? project.techManager.map(tm => tm._id || tm)
        : [],
      isActive: project.isActive,
      teamMembers: project.teamMembers?.map(member => ({
        allocation: member.allocation,
        employeeId: member.employeeId?._id || member.employeeId
      })) || []
    });
    setActiveTab('project-info');
    setCurrentView('edit');
  };

  const handleViewProject = async (project) => {
    setSelectedProject(project);
    setSelectedMilestone(project.milestone || 'requirement-gathering');
    setCurrentView('detail');
    await fetchProjectActivities(project._id);
  };

  const updateMilestone = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects/${selectedProject._id}/milestone`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ milestone: selectedMilestone }),
      });

      const data = await response.json();
      
      if (data.success) {
        setSelectedProject({ ...selectedProject, milestone: selectedMilestone });
        setEditingMilestone(false);
        message.success('Project milestone updated successfully');
        // Refresh projects list to show updated milestone
        fetchProjects();
      } else {
        message.error(data.message);
      }
    } catch (err) {
      message.error('Failed to update milestone');
      console.error('Update error:', err);
    }
  };

  const fetchProjectActivities = async (projectId) => {
    try {
      setActivitiesLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/timesheets`);
      const data = await response.json();
      
      if (data.success) {
        // Filter activities for the selected project and create timeline
        const projectTimesheets = data.data.filter(ts => 
          ts.projectId === projectId || 
          ts.projectId?._id === projectId
        );
        
        const activities = projectTimesheets.map(ts => {
          // Get employee name from employeeId object or fetch from employees list
          let employeeName = 'Unknown Employee';
          if (ts.employeeId && typeof ts.employeeId === 'object') {
            employeeName = ts.employeeId.name;
          } else if (ts.employeeId) {
            const employee = employees.find(emp => emp._id === ts.employeeId);
            employeeName = employee?.name || 'Unknown Employee';
          } else if (ts.employeeName) {
            employeeName = ts.employeeName;
          }

          return {
            id: ts._id,
            type: 'timesheet',
            title: `${employeeName} submitted timesheet`,
            description: `${ts.hours || 0}h - ${ts.description || 'No description'}`,
            status: ts.status,
            submittedOn: ts.createdAt || ts.date,
            submittedFor: ts.date,
            employee: employeeName,
            employeeId: ts.employeeId?._id || ts.employeeId,
            hours: ts.hours || 0
          };
        }).sort((a, b) => new Date(b.submittedOn) - new Date(a.submittedOn));
        
        setProjectActivities(activities);
      }
    } catch (err) {
      console.error('Error fetching project activities:', err);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'green',
      'completed': 'blue',
      'on-hold': 'orange',
      'cancelled': 'red'
    };
    return colors[status] || 'default';
  };

  // Hours breakup management functions
  const addHoursBreakupRow = () => {
    const newRow = {
      id: Date.now(),
      title: '',
      estimatedHours: 0,
      bufferPercentage: 0,
      totalHours: 0
    };
    const updatedHoursBreakup = [...hoursBreakup, newRow];
    setHoursBreakup(updatedHoursBreakup);
    
    // Update the form field with calculated total
    const newTotal = parseFloat(updatedHoursBreakup.reduce((total, row) => total + (row.totalHours || 0), 0).toFixed(2));
    form.setFieldValue('totalAllocatedHours', newTotal);
  };

  const removeHoursBreakupRow = (id) => {
    const updatedHoursBreakup = hoursBreakup.filter(row => row.id !== id);
    setHoursBreakup(updatedHoursBreakup);
    
    // Update the form field with calculated total
    const newTotal = parseFloat(updatedHoursBreakup.reduce((total, row) => total + (row.totalHours || 0), 0).toFixed(2));
    form.setFieldValue('totalAllocatedHours', newTotal);
  };

  const updateHoursBreakupRow = (id, field, value) => {
    const updatedHoursBreakup = hoursBreakup.map(row => {
      if (row.id === id) {
        const updatedRow = { ...row, [field]: value };
        // Calculate total hours when estimated hours or buffer percentage changes
        if (field === 'estimatedHours' || field === 'bufferPercentage') {
          const estimatedHours = field === 'estimatedHours' ? value : updatedRow.estimatedHours;
          const bufferPercentage = field === 'bufferPercentage' ? value : updatedRow.bufferPercentage;
          updatedRow.totalHours = parseFloat((estimatedHours + (estimatedHours * (bufferPercentage / 100))).toFixed(2));
        }
        return updatedRow;
      }
      return row;
    });
    setHoursBreakup(updatedHoursBreakup);
    
    // Update the form field with calculated total
    const newTotal = parseFloat(updatedHoursBreakup.reduce((total, row) => total + (row.totalHours || 0), 0).toFixed(2));
    form.setFieldValue('totalAllocatedHours', newTotal);
  };

  const calculateTotalProjectHours = () => {
   return parseFloat(hoursBreakup.reduce((total, row) => total + Number(row.totalHours || 0), 0).toFixed(2));
  };
  useEffect(() => {
  form.setFieldsValue({
    totalAllocatedHours: calculateTotalProjectHours()
  });
}, [hoursBreakup]);
  

  const handleCancel = () => {
    setCurrentView('list');
    setSelectedProject(null);
    setHoursBreakup([]);
    setActiveTab('project-info');
    form.resetFields();
  };

  const handleDelete = async (projectId) => {
    const result = await deleteProject(projectId);
    if (result.success) {
      message.success('Project deleted successfully');
    } else {
      message.error(result.error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      // Ensure totalAllocatedHours is calculated from hours breakup
      const calculatedTotalHours = calculateTotalProjectHours();
      
      // Filter out empty hours breakup entries
      const validHoursBreakup = hoursBreakup.filter(row => 
        row.title && row.title.trim() !== '' && row.estimatedHours > 0
      );
      
      const projectData = {
        ...values,
        startDate: values.startDate ? values.startDate.toISOString() : null,
        endDate: values.endDate ? values.endDate.toISOString() : null,
        totalAllocatedHours: parseFloat((calculatedTotalHours || 1).toFixed(2)), // Ensure minimum 1 hour with 2 decimals
        hoursBreakup: validHoursBreakup,
      };


      let result;
      if (selectedProject) {
        result = await updateProject(selectedProject._id, projectData);
        if (result.success) {
          message.success('Project updated successfully');
        } else {
          message.error(result.message || 'Failed to update project');
          return;
        }
      } else {
        result = await createProject(projectData);
        if (result.success) {
          message.success('Project created successfully');
        } else {
          message.error(result.message || 'Failed to create project');
          return;
        }
      }
      
      if (result.success) {
        setCurrentView('list');
        setSelectedProject(null);
        setHoursBreakup([]);
        setActiveTab('project-info');
        form.resetFields();
      }
    } catch (error) {
      console.error('Project submission error:', error);
      message.error('Operation failed: ' + (error.message || 'Unknown error'));
    }
  };

  const columns = [
    {
      title: 'Project Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Client',
      dataIndex: 'client',
      key: 'client',
      filters: [
        { text: 'Internal', value: 'Internal' },
        { text: 'ABC Corp', value: 'ABC Corp' },
      ],
      onFilter: (value, record) => record.client === value,
    },
    {
      title: 'Project Manager',
      dataIndex: 'projectManager',
      key: 'projectManager',
      render: (projectManagers) => {
      if (!projectManagers || projectManagers.length === 0) return 'Not assigned';

      // If array of populated objects
      if (Array.isArray(projectManagers)) {
        return projectManagers.map(pm => pm.name).join(', ');
      }

      // If it's a single object (fallback)
      if (typeof projectManagers === 'object') return projectManagers.name;

      // If it's just IDs, find them from employees
      const manager = employees.find(emp => emp._id === projectManagers);
      return manager ? manager.name : 'Unknown';
    },
      filters: employees
        .filter(emp => emp.role === 'project_manager' && emp.isActive)
        .map(emp => ({ text: emp.name, value: emp._id })),
      onFilter: (value, record) => {
        if (!record.projectManager) return false;
        if (Array.isArray(record.projectManager)) {
          return record.projectManager.some(pm => 
            (typeof pm === 'object' ? pm._id : pm) === value
          );
        }
        return (typeof record.projectManager === 'object' ? record.projectManager._id : record.projectManager) === value;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusColors = {
          active: 'bg-green-100 text-green-800',
          completed: 'bg-blue-100 text-blue-800',
          'on-hold': 'bg-yellow-100 text-yellow-800',
          cancelled: 'bg-red-100 text-red-800',
        };
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
            {status}
          </span>
        );
      },
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Completed', value: 'completed' },
        { text: 'On Hold', value: 'on-hold' },
        { text: 'Cancelled', value: 'cancelled' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Allocated Hours',
      dataIndex: 'totalAllocatedHours',
      key: 'totalAllocatedHours',
      render: (hours) => hours ? `${parseFloat(hours).toFixed(2)} hrs` : '-',
      sorter: (a, b) => (a.totalAllocatedHours || 0) - (b.totalAllocatedHours || 0),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {isActive ? 'Yes' : 'No'}
        </span>
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Project"
            description="Are you sure you want to delete this project?"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDelete(record._id);
            }}
            onCancel={(e) => e?.stopPropagation()}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderHoursBreakupTab = () => (
    <Card title={
      <div className="flex justify-between items-center">
        <span>
          <BarChartOutlined className="mr-2" />
          Hours Breakup & Milestones
        </span>
        <div className="text-lg font-bold text-blue-600">
          Total: {calculateTotalProjectHours().toFixed(1)} hours
        </div>
      </div>
    }>
      {/* Total Hours Summary */}
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-1">
                {calculateTotalProjectHours().toFixed(1)}h
              </div>
              <div className="text-sm font-semibold text-gray-700">
                TOTAL ESTIMATED HOURS
              </div>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {hoursBreakup.reduce((sum, row) => sum + (row.estimatedHours || 0), 0).toFixed(1)}h
              </div>
              <div className="text-sm font-semibold text-gray-700">
                BASE HOURS
              </div>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600 mb-1">
                {(calculateTotalProjectHours() - hoursBreakup.reduce((sum, row) => sum + (row.estimatedHours || 0), 0)).toFixed(1)}h
              </div>
              <div className="text-sm font-semibold text-gray-700">
                BUFFER HOURS
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Hours Breakup Table */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>Project Milestones & Hours</Title>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={addHoursBreakupRow}
          >
            Add Milestone
          </Button>
        </div>
        
        {hoursBreakup.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileTextOutlined className="text-4xl mb-2" />
            <div>No milestones added yet</div>
            <div className="text-sm">Click "Add Milestone" to start planning your project hours</div>
          </div>
        ) : (
          <div className="space-y-4">
            {hoursBreakup.map((row, index) => (
              <Card key={row.id} size="small" className="border-l-4 border-l-blue-500">
                <Row gutter={[16, 16]} align="middle">
                  <Col xs={24} sm={8}>
                    <div className="mb-2">
                      <Text strong>Milestone Title</Text>
                    </div>
                    <Input
                      placeholder="e.g., Requirements Analysis, Development, Testing"
                      value={row.title}
                      onChange={(e) => updateHoursBreakupRow(row.id, 'title', e.target.value)}
                      size="large"
                    />
                  </Col>
                  <Col xs={24} sm={4}>
                    <div className="mb-2">
                      <Text strong>Est. Hours</Text>
                    </div>
                    <InputNumber
                      placeholder="Hours"
                      value={row.estimatedHours}
                      onChange={(value) => updateHoursBreakupRow(row.id, 'estimatedHours', value || 0)}
                      min={0}
                      className="w-full"
                      size="large"
                      formatter={value => `${value}h`}
                      parser={value => value.replace('h', '')}
                    />
                  </Col>
                  <Col xs={24} sm={4}>
                    <div className="mb-2">
                      <Text strong>Buffer %</Text>
                    </div>
                    <InputNumber
                      placeholder="Buffer"
                      value={row.bufferPercentage}
                      onChange={(value) => updateHoursBreakupRow(row.id, 'bufferPercentage', value || 0)}
                      min={0}
                      max={100}
                      className="w-full"
                      size="large"
                      formatter={value => `${value}%`}
                      parser={value => value.replace('%', '')}
                    />
                  </Col>
                  <Col xs={24} sm={4}>
                    <div className="mb-2">
                      <Text strong>Total Hours</Text>
                    </div>
                    <div className="text-lg font-bold text-blue-600 bg-blue-50 p-2 rounded text-center">
                      {row.totalHours.toFixed(1)}h
                    </div>
                  </Col>
                  <Col xs={24} sm={4}>
                    <div className="mb-2">
                      <Text strong>Action</Text>
                    </div>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeHoursBreakupRow(row.id)}
                      size="large"
                      className="w-full"
                    >
                      Remove
                    </Button>
                  </Col>
                </Row>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Card>
  );

  const renderProjectForm = () => (
    <div>
      <div className="mb-4 flex justify-end">
        <Space size="large">
          <Button icon={<CloseOutlined />} onClick={handleCancel} size="large">
            Cancel
          </Button>
          <Button 
            type="primary" 
            icon={<SaveOutlined />} 
            onClick={() => form.submit()}
            loading={loading}
            size="large"
          >
            {selectedProject ? 'Update' : 'Create'} Project
          </Button>
        </Space>
      </div>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={[
          {
            key: 'project-info',
            label: (
              <span>
                <ProjectOutlined />
                Project Information
              </span>
            ),
            children: (
              <Row gutter={[24, 24]}>
                {/* Left Column - Project Details */}
                <Col xs={24} lg={12}>
                  <Card title="Project Information" className="h-full">
            <Form.Item
              name="name"
              label="Project Name"
              rules={[{ required: true, message: 'Please enter project name' }]}
            >
              <Input placeholder="Enter project name" size="large" />
            </Form.Item>

            <Form.Item
              name="client"
              label="Client"
              rules={[{ required: true, message: 'Please enter client name' }]}
            >
              <Input placeholder="Enter client name" size="large" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea 
                placeholder="Enter project description" 
                rows={4}
                size="large"
              />
            </Form.Item>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: 'Please select status' }]}
                  initialValue="active"
                >
                  <Select placeholder="Select status" size="large">
                    <Option value="active">Active</Option>
                    <Option value="completed">Completed</Option>
                    <Option value="on-hold">On Hold</Option>
                    <Option value="cancelled">Cancelled</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="isActive"
                  label="Active Status"
                  rules={[{ required: true, message: 'Please select active status' }]}
                  initialValue={true}
                >
                  <Select placeholder="Select active status" size="large">
                    <Option value={true}>Active</Option>
                    <Option value={false}>Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="startDate"
                  label="Start Date"
                >
                  <DatePicker className="w-full" size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="endDate"
                  label="End Date"
                >
                  <DatePicker className="w-full" size="large" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="totalAllocatedHours"
              label="Total Allocated Hours (Calculated from Hours Breakup)"
            >
              <InputNumber 
                placeholder="Calculated from Hours Breakup tab" 
                // value={calculateTotalProjectHours()}
                className="w-full"
                size="large"
                formatter={value => `${value} hrs`}
                parser={value => value.replace(' hrs', '')}
                disabled
                style={{ backgroundColor: '#f5f5f5', color: '#666' }}
              />
            </Form.Item>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="projectManager"
                  label="Project Manager"
                  rules={[{ required: true, message: 'Please select a project manager' }]}
                >
                  <Select 
                    mode="multiple" 
                    placeholder="Select project manager" 
                    size="large"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                      return String(option.children).toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                  >
                    {employees.filter(emp => emp.role === 'project_manager' && emp.isActive).map(manager => (
                      <Option key={manager._id} value={manager._id}>
                        {manager.name} - {manager.position}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="techManager"
                  label="Tech Manager"
                  rules={[{ required: true, message: 'Please select a tech manager' }]}
                >
                  <Select 
                    mode="multiple" 
                    placeholder="Select tech manager" 
                    size="large"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                      return String(option.children).toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                  >
                    {employees.filter(emp => (emp.role === 'project_manager' || emp.role === 'employee') && emp.isActive).map(manager => (
                      <Option key={manager._id} value={manager._id}>
                        {manager.name} - {manager.position}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>

      {/* Right Column - Team Assignment */}
      <Col xs={24} lg={12}>
        <Card 
          title={
            <span>
              <UserOutlined className="mr-2" />
              Team Assignment
            </span>
          } 
          className="h-full"
        >
          <Form.List name="teamMembers">
            {(fields, { add, remove }) => (
                <>
                  {/* Display existing team members as cards */}
                  {fields.map(({ key, name, ...restField }) => {
                    const memberValue = form.getFieldValue(['teamMembers', name]);
                    // Handle both object and string employee ID formats
                    const employeeId = memberValue?.employeeId?._id || memberValue?.employeeId;
                    const employee = employees.find(emp => emp._id === employeeId);
                    const isEditing = editingMember === key;
                    
                    return (
                      <div key={key} className="mb-4">
                        {employeeId && !isEditing ? (
                          // Card view for added members
                          <Card 
                            className="border border-gray-200 hover:shadow-md transition-shadow"
                            bodyStyle={{ padding: '16px' }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <Avatar 
                                  size={40} 
                                  icon={<UserOutlined />}
                                  style={{ backgroundColor: '#1890ff' }}
                                >
                                  {employee?.name?.charAt(0)}
                                </Avatar>
                                <div>
                                  <Text strong className="text-base">{employee?.name || 'Unknown Employee'}</Text>
                                  <br />
                                  <Text type="secondary" className="text-sm">{employee?.position}</Text>
                                </div>
                              </div>
                              <div className="flex items-center space-x-3">
                                <Tag 
                                  color={memberValue.allocation >= 80 ? 'red' : memberValue.allocation >= 60 ? 'orange' : 'blue'}
                                  className="text-sm px-3 py-1"
                                >
                                  {memberValue.allocation}% Allocated
                                </Tag>
                                <Button
                                  type="text"
                                  icon={<EditOutlined />}
                                  onClick={() => setEditingMember(key)}
                                  className="text-blue-600 hover:text-blue-800"
                                />
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => remove(name)}
                                  className="text-red-600 hover:text-red-800"
                                />
                              </div>
                            </div>
                          </Card>
                        ) : (
                          // Form view for editing or new members
                          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                            <Row gutter={[16, 16]} align="top">
                              <Col xs={24} sm={12}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'employeeId']}
                                  label="Team Member"
                                  rules={[{ required: true, message: 'Please select team member' }]}
                                >
                                  <Select 
                                    placeholder="Select employee" 
                                    size="large"
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) => {
                                      return String(option.children).toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                    }}
                                  >
                                    {/* emp.role === 'employee' && */}
                                    {employees.filter(emp => emp.isActive).map(employee => ( 
                                      <Option key={employee._id} value={employee._id}>
                                        {employee.name} - {employee.position}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col xs={12} sm={8}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'allocation']}
                                  label="Allocation"
                                  rules={[
                                    { required: true, message: 'Please enter allocation' },
                                    { type: 'number', min: 1, max: 100, message: 'Allocation must be between 1-100%' }
                                  ]}
                                >
                                  <InputNumber
                                    placeholder="Enter %"
                                    min={1}
                                    max={100}
                                    className="w-full"
                                    size="large"
                                    addonAfter="%"
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={12} sm={4}>
                                <Form.Item label=" " colon={false}>
                                  <Space>
                                    {isEditing && (
                                      <Button
                                        type="primary"
                                        icon={<SaveOutlined />}
                                        onClick={() => setEditingMember(null)}
                                        size="small"
                                      />
                                    )}
                                    <Button
                                      type="text"
                                      danger
                                      icon={isEditing ? <CloseOutlined /> : <MinusCircleOutlined />}
                                      onClick={() => isEditing ? setEditingMember(null) : remove(name)}
                                    />
                                  </Space>
                                </Form.Item>
                              </Col>
                            </Row>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      size="large"
                    >
                      Add Team Member
                    </Button>
                  </Form.Item>
                </>
            )}
          </Form.List>
          
          <Divider />
          
          <div className="text-center">
            <Typography.Text type="secondary">
              Assign team members with their allocation percentage for this project
            </Typography.Text>
          </div>
        </Card>
      </Col>
      </Row>
            )
          },
          {
            key: 'hours-breakup',
            label: (
              <span>
                <BarChartOutlined />
                Hours Breakup
              </span>
            ),
            children: renderHoursBreakupTab()
          }
        ]}
      />
      
      </Form>
    </div>
  );

  const ProjectDetailView = () => {
    const [projectStats, setProjectStats] = useState({
      totalHours: 0,
      totalTimesheets: 0,
      pendingTimesheets: 0,
      approvedTimesheets: 0,
      teamSize: 0,
      allocatedHours: 0,
      progressPercentage: 0,
      remainingHours: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      if (selectedProject?._id) {
        calculateProjectStats();
      }
    }, [selectedProject, projectActivities]);

    const calculateProjectStats = () => {
      setLoading(true);
      try {
        const timesheets = projectActivities;
        const totalHours = timesheets.reduce((sum, ts) => sum + (ts.hours || 0), 0);
        const pendingTimesheets = timesheets.filter(ts => ts.status === 'submitted' || ts.status === 'pending').length;
        const approvedTimesheets = timesheets.filter(ts => ts.status === 'approved').length;
        const allocatedHours = selectedProject?.totalAllocatedHours || 0;
        const progressPercentage = allocatedHours > 0 ? Math.round((totalHours / allocatedHours) * 100) : 0;
        
        setProjectStats({
          totalHours,
          totalTimesheets: timesheets.length,
          pendingTimesheets,
          approvedTimesheets,
          teamSize: selectedProject?.teamMembers?.length || 0,
          allocatedHours,
          progressPercentage,
          remainingHours: Math.max(0, allocatedHours - totalHours)
        });
      } catch (error) {
        console.error('Error calculating project stats:', error);
      } finally {
        setLoading(false);
      }
    };

    const tabItems = [
      {
        key: 'overview',
        label: (
          <span>
            <ProjectOutlined />
            Overview
          </span>
        ),
        children: (
          <Row gutter={24}>
            {/* Project Details - Full Width */}
            <Col span={24}>
              <Card>
                {/* Project Progress Header - Very Prominent */}
                <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">

              <Row gutter={[24, 24]} align="middle">
                <Col xs={24} md={8}>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-blue-600 mb-2">
                      {projectStats.progressPercentage}%
                    </div>
                    <div className="text-lg font-semibold text-gray-700">
                      PROJECT COMPLETION
                    </div>
                    <Progress 
                      percent={projectStats.progressPercentage} 
                      size="default"
                      strokeColor={{
                        '0%': projectStats.progressPercentage > 100 ? '#ff4d4f' : '#108ee9',
                        '100%': projectStats.progressPercentage > 100 ? '#ff7875' : '#87d068',
                      }}
                      status={projectStats.progressPercentage > 100 ? 'exception' : 'active'}
                      className="mt-2"
                    />
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      {projectStats.totalHours}h
                    </div>
                    <div className="text-lg font-semibold text-gray-700">
                      HOURS INVESTED
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Total time logged by team
                    </div>
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600 mb-2">
                      {projectStats.allocatedHours}h
                    </div>
                    <div className="text-lg font-semibold text-gray-700">
                      HOURS ALLOCATED
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Total budget allocation
                    </div>
                  </div>
                </Col>
              </Row>
              
              {/* Progress Status Alert */}
              {projectStats.progressPercentage > 100 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-red-600 font-semibold">⚠️ OVER BUDGET:</span>
                    <span className="text-red-700">
                      Project has exceeded allocated hours by {projectStats.totalHours - projectStats.allocatedHours} hours
                    </span>
                  </div>
                </div>
              )}
              
              {projectStats.progressPercentage >= 80 && projectStats.progressPercentage <= 100 && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-yellow-600 font-semibold">⚡ NEAR COMPLETION:</span>
                    <span className="text-yellow-700">
                      {projectStats.remainingHours} hours remaining in budget
                    </span>
                  </div>
                </div>
              )}
            </div>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Typography.Text strong>Client:</Typography.Text>
                <div className="mt-1">{selectedProject.client}</div>
              </Col>
              <Col span={12}>
                <Typography.Text strong>Status:</Typography.Text>
                <div className="mt-1">
                  <Tag color={getStatusColor(selectedProject.status)}>
                    {selectedProject.status.charAt(0).toUpperCase() + selectedProject.status.slice(1)}
                  </Tag>
                </div>
              </Col>
              {selectedProject.startDate && (
                <Col span={12}>
                  <Typography.Text strong>Start Date:</Typography.Text>
                  <div className="mt-1">{dayjs(selectedProject.startDate).format('MMM DD, YYYY')}</div>
                </Col>
              )}
              {selectedProject.endDate && (
                <Col span={12}>
                  <Typography.Text strong>End Date:</Typography.Text>
                  <div className="mt-1">{dayjs(selectedProject.endDate).format('MMM DD, YYYY')}</div>
                </Col>
              )}
              {selectedProject.totalAllocatedHours && (
                <Col span={12}>
                  <Typography.Text strong>Total Allocated Hours:</Typography.Text>
                  <div className="mt-1">{parseFloat(selectedProject.totalAllocatedHours).toFixed(2)} hrs</div>
                </Col>
              )}
              <Col span={12}>
                <Typography.Text strong>Project Manager:</Typography.Text>
                <div className="mt-1">
                  {selectedProject.projectManager ? 
                    (typeof selectedProject.projectManager === 'object' ? 
                      selectedProject.projectManager.name : 
                      employees.find(emp => emp._id === selectedProject.projectManager)?.name || 'Unknown'
                    ) : 
                    'Not assigned'
                  }
                </div>
              </Col>
              <Col span={12}>
                <Typography.Text strong>Tech Manager:</Typography.Text>
                <div className="mt-1">
                  {selectedProject.techManager ? 
                    (typeof selectedProject.techManager === 'object' ? 
                      selectedProject.techManager.name : 
                      employees.find(emp => emp._id === selectedProject.techManager)?.name || 'Unknown'
                    ) : 
                    'Not assigned'
                  }
                </div>
              </Col>
              <Col span={12}>
                <Typography.Text strong>Created:</Typography.Text>
                <div className="mt-1">{dayjs(selectedProject.createdAt).format('MMM DD, YYYY')}</div>
              </Col>
            </Row>
            {selectedProject.description && (
              <div className="mt-4">
                <Typography.Text strong>Description:</Typography.Text>
                <div className="mt-2 p-3 bg-gray-50 rounded">
                  {selectedProject.description}
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>
        ),
      },
      {
        key: 'statistics',
        label: (
          <span>
            <BarChartOutlined />
            Statistics
          </span>
        ),
        children: (
          <Card loading={loading}>
            {/* Prominent Progress Display */}
            <div className="mb-8 p-8 bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 rounded-xl border-2 border-indigo-200">
              <div className="text-center mb-6">
                <div className="text-5xl font-bold text-indigo-600 mb-3">
                  {projectStats.progressPercentage}%
                </div>
                <div className="text-2xl font-bold text-gray-800 mb-4">
                  PROJECT COMPLETION RATE
                </div>
                <Progress 
                  percent={projectStats.progressPercentage} 
                  size="large"
                  strokeWidth={12}
                  strokeColor={{
                    '0%': projectStats.progressPercentage > 100 ? '#ff4d4f' : '#1890ff',
                    '100%': projectStats.progressPercentage > 100 ? '#ff7875' : '#52c41a',
                  }}
                  status={projectStats.progressPercentage > 100 ? 'exception' : 'active'}
                  className="mb-4"
                />
              </div>
              
              <Row gutter={[32, 16]} className="text-center">
                <Col xs={24} sm={8}>
                  <div className="p-4 bg-white rounded-lg shadow-sm border">
                    <div className="text-4xl font-bold text-green-600 mb-2">
                      {projectStats.totalHours}
                    </div>
                    <div className="text-lg font-semibold text-gray-700">
                      HOURS INVESTED
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Total logged by team
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={8}>
                  <div className="p-4 bg-white rounded-lg shadow-sm border">
                    <div className="text-4xl font-bold text-purple-600 mb-2">
                      {projectStats.allocatedHours}
                    </div>
                    <div className="text-lg font-semibold text-gray-700">
                      HOURS ALLOCATED
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Project budget
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={8}>
                  <div className="p-4 bg-white rounded-lg shadow-sm border">
                    <div className={`text-4xl font-bold mb-2 ${
                      projectStats.remainingHours < 0 ? 'text-red-600' : 'text-orange-600'
                    }`}>
                      {Math.abs(projectStats.remainingHours)}
                    </div>
                    <div className="text-lg font-semibold text-gray-700">
                      {projectStats.remainingHours < 0 ? 'HOURS OVER' : 'HOURS REMAINING'}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {projectStats.remainingHours < 0 ? 'Budget exceeded' : 'Budget left'}
                    </div>
                  </div>
                </Col>
              </Row>
              
              {/* Status Alerts */}
              {projectStats.progressPercentage > 100 && (
                <div className="mt-6 p-4 bg-red-100 border-2 border-red-300 rounded-lg">
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-2xl">🚨</span>
                    <div>
                      <div className="text-xl font-bold text-red-700">BUDGET EXCEEDED</div>
                      <div className="text-red-600">
                        Project is {projectStats.totalHours - projectStats.allocatedHours} hours over budget
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {projectStats.progressPercentage >= 80 && projectStats.progressPercentage <= 100 && (
                <div className="mt-6 p-4 bg-yellow-100 border-2 border-yellow-300 rounded-lg">
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-2xl">⚡</span>
                    <div>
                      <div className="text-xl font-bold text-yellow-700">NEARING COMPLETION</div>
                      <div className="text-yellow-600">
                        Only {projectStats.remainingHours} hours remaining in budget
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Team Members"
                  value={projectStats.teamSize}
                  prefix={<TeamOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Total Timesheets"
                  value={projectStats.totalTimesheets}
                  prefix={<FileTextOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Approval Rate"
                  value={projectStats.totalTimesheets > 0 
                    ? Math.round((projectStats.approvedTimesheets / projectStats.totalTimesheets) * 100)
                    : 0}
                  suffix="%"
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Pending Approvals"
                  value={projectStats.pendingTimesheets}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Col>
            </Row>
          </Card>
        ),
      },
      {
        key: 'team',
        label: (
          <span>
            <TeamOutlined />
            Team
          </span>
        ),
        children: (
          <Card>
            {/* Project Leadership Section */}
            <div className="mb-6">
              <Typography.Title level={4}>Project Leadership</Typography.Title>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Card size="small" className="bg-blue-50">
                    <div className="flex items-center gap-3">
                      <Avatar icon={<CrownOutlined />} className="bg-blue-500" />
                      <div>
                        <Typography.Text strong>Project Manager</Typography.Text>
                        <div className="text-sm">
                          {selectedProject.projectManager ? 
                            (typeof selectedProject.projectManager === 'object' ? 
                              selectedProject.projectManager.name : 
                              employees.find(emp => emp._id === selectedProject.projectManager)?.name || 'Unknown'
                            ) : 
                            'Not assigned'
                          }
                        </div>
                        <div className="text-xs text-gray-500">
                          {(() => {
                            const pmId = typeof selectedProject.projectManager === 'object' ? 
                              selectedProject.projectManager._id : selectedProject.projectManager;
                            const pmTimesheets = projectActivities.filter(activity => 
                              activity.employeeId === pmId
                            );
                            const pmHours = pmTimesheets.reduce((sum, ts) => sum + (ts.hours || 0), 0);
                            return `${pmHours} hours contributed`;
                          })()}
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card size="small" className="bg-green-50">
                    <div className="flex items-center gap-3">
                      <Avatar icon={<ToolOutlined />} className="bg-green-500" />
                      <div>
                        <Typography.Text strong>Tech Manager</Typography.Text>
                        <div className="text-sm">
                          {selectedProject.techManager ? 
                            (typeof selectedProject.techManager === 'object' ? 
                              selectedProject.techManager.name : 
                              employees.find(emp => emp._id === selectedProject.techManager)?.name || 'Unknown'
                            ) : 
                            'Not assigned'
                          }
                        </div>
                        <div className="text-xs text-gray-500">
                          {(() => {
                            const tmId = typeof selectedProject.techManager === 'object' ? 
                              selectedProject.techManager._id : selectedProject.techManager;
                            const tmTimesheets = projectActivities.filter(activity => 
                              activity.employeeId === tmId
                            );
                            const tmHours = tmTimesheets.reduce((sum, ts) => sum + (ts.hours || 0), 0);
                            return `${tmHours} hours contributed`;
                          })()}
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Team Members Section */}
            <div>
              <Typography.Title level={4}>Team Members</Typography.Title>
              {selectedProject.teamMembers && selectedProject.teamMembers.length > 0 ? (
                <List
                  dataSource={Array.isArray(selectedProject.teamMembers) ? selectedProject.teamMembers : []}
                  renderItem={(member) => {
                    // Try multiple ways to find the employee
                    let employee = employees.find(emp => emp._id === member.employeeId);
                    
                    // If not found by direct ID match, try string comparison
                    if (!employee && member.employeeId) {
                      employee = employees.find(emp => emp._id.toString() === member.employeeId.toString());
                    }
                    
                    // If still not found, try by nested employeeId
                    if (!employee && member.employeeId?._id) {
                      employee = employees.find(emp => emp._id === member.employeeId._id);
                    }
                    
                    const memberTimesheets = projectActivities.filter(activity => 
                      activity.employeeId === member.employeeId ||
                      activity.employeeId === member.employeeId?._id
                    );
                    const memberHours = memberTimesheets.reduce((sum, ts) => sum + (ts.hours || 0), 0);
                    
                    return (
                      <List.Item>
                        <List.Item.Meta
                          avatar={<Avatar icon={<UserOutlined />} />}
                          title={
                            <div className="flex items-center gap-2">
                              <span>{employee?.name || `Employee ID: ${member.employeeId}`}</span>
                              <Tag color="blue">{employee?.role?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}</Tag>
                            </div>
                          }
                          description={
                            <div>
                              <Typography.Text type="secondary">{employee?.email || 'No email found'}</Typography.Text>
                              <div className="mt-2 flex gap-2">
                                <Tag color="green">{member.allocation}% Allocation</Tag>
                                <Tag color="orange">{memberHours} hours contributed</Tag>
                                <Tag color="purple">
                                  {memberTimesheets.length} timesheet{memberTimesheets.length !== 1 ? 's' : ''}
                                </Tag>
                              </div>
                            </div>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              ) : (
                <Alert message="No team members assigned to this project" type="info" />
              )}
            </div>
          </Card>
        ),
      },
      {
        key: 'hours-breakup-view',
        label: (
          <span>
            <BarChartOutlined />
            Hours Breakup
          </span>
        ),
        children: (
          <Card title={
            <div className="flex justify-between items-center">
              <span>
                <BarChartOutlined className="mr-2" />
                Project Hours Breakdown & Milestones
              </span>
              {selectedProject.hoursBreakup && selectedProject.hoursBreakup.length > 0 && (
                <div className="text-lg font-bold text-blue-600">
                  Total: {selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0).toFixed(1)} hours
                </div>
              )}
            </div>
          }>
            {selectedProject.hoursBreakup && selectedProject.hoursBreakup.length > 0 ? (
              <>
                {/* Summary Cards */}
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={8}>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-blue-600 mb-1">
                          {selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0).toFixed(1)}h
                        </div>
                        <div className="text-sm font-semibold text-gray-700">
                          TOTAL PLANNED HOURS
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-green-600 mb-1">
                          {selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.estimatedHours || 0), 0).toFixed(1)}h
                        </div>
                        <div className="text-sm font-semibold text-gray-700">
                          BASE HOURS
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-orange-600 mb-1">
                          {(selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0) - 
                            selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.estimatedHours || 0), 0)).toFixed(1)}h
                        </div>
                        <div className="text-sm font-semibold text-gray-700">
                          BUFFER HOURS
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Milestones List */}
                <div className="space-y-4">
                  <Title level={4}>Project Milestones</Title>
                  {selectedProject.hoursBreakup.map((milestone, index) => (
                    <Card key={milestone.id || index} size="small" className="border-l-4 border-l-blue-500">
                      <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={8}>
                          <div>
                            <Text strong className="text-lg">{milestone.title}</Text>
                            <div className="text-sm text-gray-500 mt-1">
                              Milestone #{index + 1}
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={4}>
                          <div className="text-center">
                            <div className="text-xl font-bold text-green-600">
                              {milestone.estimatedHours}h
                            </div>
                            <div className="text-xs text-gray-500">Base Hours</div>
                          </div>
                        </Col>
                        <Col xs={24} sm={4}>
                          <div className="text-center">
                            <div className="text-xl font-bold text-orange-600">
                              {milestone.bufferPercentage}%
                            </div>
                            <div className="text-xs text-gray-500">Buffer</div>
                          </div>
                        </Col>
                        <Col xs={24} sm={4}>
                          <div className="text-center">
                            <div className="text-xl font-bold text-blue-600">
                              {milestone.totalHours.toFixed(1)}h
                            </div>
                            <div className="text-xs text-gray-500">Total Hours</div>
                          </div>
                        </Col>
                        <Col xs={24} sm={4}>
                          <div className="text-center">
                            <Progress 
                              type="circle" 
                              size={60}
                              percent={selectedProject.totalAllocatedHours > 0 ? 
                                Math.round((milestone.totalHours / selectedProject.totalAllocatedHours) * 100) : 0}
                              format={(percent) => `${percent}%`}
                              strokeColor="#1890ff"
                            />
                            <div className="text-xs text-gray-500 mt-1">of Total</div>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </div>

                {/* Comparison with Actual Hours */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <Title level={5}>Planning vs Actual Comparison</Title>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                      <Statistic
                        title="Planned Hours"
                        value={selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0).toFixed(1)}
                        suffix="h"
                        valueStyle={{ color: '#1890ff' }}
                      />
                    </Col>
                    <Col xs={24} sm={8}>
                      <Statistic
                        title="Actual Hours Logged"
                        value={projectStats.totalHours}
                        suffix="h"
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Col>
                    <Col xs={24} sm={8}>
                      <Statistic
                        title="Variance"
                        value={Math.abs(projectStats.totalHours - selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0)).toFixed(1)}
                        suffix="h"
                        valueStyle={{ 
                          color: projectStats.totalHours > selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0) ? '#ff4d4f' : '#52c41a' 
                        }}
                        prefix={projectStats.totalHours > selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0) ? '+' : '-'}
                      />
                    </Col>
                  </Row>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileTextOutlined className="text-6xl mb-4" />
                <div className="text-xl mb-2">No Hours Breakup Available</div>
                <div className="text-sm">
                  This project was created without milestone planning. 
                  Edit the project to add hours breakup and milestone details.
                </div>
              </div>
            )}
          </Card>
        ),
      },
    ];

    return (
      <div>
        <div className="mb-4">
          <div className="flex justify-between items-start mb-4">
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleCancel}
            >
              Back to Projects
            </Button>
            <Button 
              type="primary"
              icon={<EditOutlined />} 
              onClick={() => handleEdit(selectedProject)}
              size="large"
            >
              Edit Project
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <ProjectOutlined className="text-2xl text-blue-500" />
            <Title level={2} className="mb-0">{selectedProject?.name}</Title>
            <Tag color={getStatusColor(selectedProject?.status)} className="ml-2">
              {selectedProject?.status?.charAt(0).toUpperCase() + selectedProject?.status?.slice(1)}
            </Tag>
          </div>
        </div>
        <Tabs defaultActiveKey="overview" items={tabItems} />
      </div>
    );
  };

  const ProjectActivitiesSidebar = () => (
    <Card className="h-full">
      <Tabs
        defaultActiveKey="activities"
        size="small"
        items={[
          {
            key: 'activities',
            label: (
              <span>
                <BulbOutlined />
                Activities
              </span>
            ),
            children: (
              <div>
                {activitiesLoading ? (
                  <div className="flex justify-center py-8">
                    <Spin />
                  </div>
                ) : projectActivities.length > 0 ? (
                  <Timeline
                    size="small"
                    items={projectActivities.slice(0, 10).map(activity => ({
                      dot: activity.status === 'approved' ? 
                        <CheckCircleOutlined className="text-green-500" /> : 
                        activity.status === 'rejected' ? 
                        <CloseCircleOutlined className="text-red-500" /> :
                        <ClockCircleOutlined className="text-orange-500" />,
                      children: (
                        <div>
                          <div className="font-medium text-sm">{activity.title}</div>
                          <div className="text-xs text-gray-500">{activity.description}</div>
                          <div className="text-xs text-gray-400 mt-1">
                            <div>Submitted On: {dayjs(activity.submittedOn).format('MMM DD, YYYY hh:mm A')}</div>
                            <div>Submitted For: {dayjs(activity.submittedFor).format('MMM DD, YYYY')}</div>
                          </div>
                        </div>
                      ),
                    }))}
                  />
                ) : (
                  <div className="text-center py-4">
                    <BulbOutlined className="text-2xl text-gray-300 mb-2" />
                    <div className="text-sm text-gray-500">No recent activities</div>
                  </div>
                )}
              </div>
            )
          },
          {
            key: 'milestones',
            label: (
              <span>
                <ProjectOutlined />
                Milestones
              </span>
            ),
            children: (
              <div>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <Tag 
                      color={getMilestoneColor(selectedProject.milestone || 'requirement-gathering')} 
                      className="px-2 py-1 text-xs font-medium"
                    >
                      {getMilestoneLabel(selectedProject.milestone || 'requirement-gathering')}
                    </Tag>
                    <Button 
                      type="link" 
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => setEditingMilestone(true)}
                    >
                      Edit
                    </Button>
                  </div>
                  
                  {editingMilestone && (
                    <div className="mb-3 p-2 bg-gray-50 rounded border">
                      <Select
                        value={selectedMilestone}
                        onChange={setSelectedMilestone}
                        className="w-full mb-2"
                        size="small"
                      >
                        {MILESTONE_OPTIONS.map(milestone => (
                          <Select.Option key={milestone.key} value={milestone.key}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-2 h-2 rounded-full" 
                                style={{ backgroundColor: milestone.color }}
                              />
                              <span className="text-xs">{milestone.label}</span>
                            </div>
                          </Select.Option>
                        ))}
                      </Select>
                      <div className="flex gap-1">
                        <Button 
                          type="primary" 
                          size="small"
                          icon={<SaveOutlined />}
                          onClick={updateMilestone}
                        >
                          Save
                        </Button>
                        <Button 
                          size="small"
                          onClick={() => {
                            setEditingMilestone(false);
                            setSelectedMilestone(selectedProject.milestone || 'requirement-gathering');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Vertical Steps */}
                  <Steps
                    direction="vertical"
                    current={MILESTONE_OPTIONS.findIndex(m => m.key === (selectedProject.milestone || 'requirement-gathering'))}
                    size="small"
                    items={MILESTONE_OPTIONS.map(milestone => ({
                      title: <span className="text-xs">{milestone.label.split(' ')[0]}</span>,
                      description: milestone.key === (selectedProject.milestone || 'requirement-gathering') ? 
                        <span className="text-xs text-blue-600 font-medium">Current</span> : null
                    }))}
                  />
                </div>
              </div>
            )
          },
          {
            key: 'tasks',
            label: (
              <span>
                <CheckCircleOutlined />
                Tasks
              </span>
            ),
            children: (
              <div>
                <div className="text-center py-8">
                  <CheckCircleOutlined className="text-4xl text-gray-300 mb-3" />
                  <div className="text-sm text-gray-500">
                    Task management coming soon
                  </div>
                </div>
              </div>
            )
          }
        ]}
      />
    </Card>
  );

  const renderProjectDetail = () => {
    return (
      <Row gutter={[24, 0]} className="h-full">
        {/* Main Content - 70% */}
        <Col span={17}>
          <ProjectDetailView />
        </Col>
        
        {/* Activities Sidebar - 30% */}
        <Col span={7}>
          <ProjectActivitiesSidebar />
        </Col>
      </Row>
    );
  };

  return (
    <div className="p-6">
      {currentView !== 'detail' && (
        <div className="mb-6">
          <Title level={2}>
            <ProjectOutlined className="mr-2" />
            {currentView === 'list' ? 'Project Management' :
             currentView === 'create' ? 'Create New Project' :
             currentView === 'edit' ? 'Edit Project' :
             'Project Details'}
          </Title>
        </div>
      )}

      {currentView === 'list' && (
        <Card>
          {/* Search and Filter Section */}
          <div className="mb-4">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Input
                  placeholder="Search by project name, client, or description..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="large"
                  allowClear
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={4}>
                <Select
                  placeholder="Status"
                  value={filterStatus}
                  onChange={setFilterStatus}
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Option value="all">All Status</Option>
                  <Option value="active">Active</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="on-hold">On Hold</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4}>
                <Select
                  placeholder="Client"
                  value={filterClient}
                  onChange={setFilterClient}
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Option value="all">All Clients</Option>
                  {clients.map(client => (
                    <Option key={client} value={client}>{client}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4}>
                <Select
                  placeholder="Active Status"
                  value={filterActive}
                  onChange={setFilterActive}
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Option value="all">All Projects</Option>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Col>
              <Col xs={12} sm={12} md={6} lg={4}>
                <Space>
                  <Button
                    icon={<ClearOutlined />}
                    onClick={handleClearFilters}
                    size="large"
                  >
                    Clear
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                    size="large"
                  >
                    Add
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>

          {/* Results Summary */}
          <div className="mb-4">
            <Text type="secondary">
              Showing {filteredProjects.length} of {projects.length} projects
              {(searchText || filterStatus !== 'all' || filterClient !== 'all' || filterActive !== 'all') && (
                <Tag color="blue" className="ml-2">
                  <FilterOutlined /> Filtered
                </Tag>
              )}
            </Text>
          </div>

          <Table
            columns={columns}
            dataSource={filteredProjects}
            rowKey="_id"
            loading={loading}
            onRow={(record) => ({
              onClick: () => handleViewProject(record),
              style: { cursor: 'pointer' },
              className: 'hover:bg-gray-50 transition-colors duration-200'
            })}
            pagination={{
              current: tablePagination.current,
              pageSize: tablePagination.pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `Total ${total} projects`,
            }}
            onChange={(pagination, filters, sorter) => {
              setTablePagination({
                current: pagination.current,
                pageSize: pagination.pageSize,
              });
              setTableFilters(filters);
              setTableSorter(sorter);
            }}
            scroll={{ x: 800 }}
          />
        </Card>
      )}

      {(currentView === 'create' || currentView === 'edit') && renderProjectForm()}
      {currentView === 'detail' && renderProjectDetail()}
    </div>
  );
};

export default ProjectManagement;
